import { useState } from "react";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { Loader2, RefreshCw, Check, AlertCircle } from "lucide-react";

export const TwitterSync = () => {
  const { toast } = useToast();
  const [isSyncingProfiles, setSyncingProfiles] = useState(false);
  const [syncProfilesStatus, setSyncProfilesStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [syncProfilesResult, setSyncProfilesResult] = useState<any>(null);
  const [errorDetails, setErrorDetails] = useState<string | null>(null);

  const handleSyncProfiles = async () => {
    try {
      setSyncingProfiles(true);
      setSyncProfilesStatus('idle');
      setSyncProfilesResult(null);
      setErrorDetails(null);

      toast({
        title: "Syncing Twitter Profiles",
        description: "Starting synchronization of Twitter KOL profiles...",
      });

      console.log("Calling twitter-sync-kol-profiles function");
      const response = await supabase.functions.invoke('twitter-sync-kol-profiles');
      console.log("Function response:", response);

      if (response.error) {
        throw new Error(response.error.message || "An error occurred during synchronization");
      }

      const result = response.data;
      
      if (!result || !result.success) {
        throw new Error(result?.message || "Synchronization failed");
      }

      setSyncProfilesResult(result);
      setSyncProfilesStatus('success');
      
      toast({
        title: "Twitter Profiles Synced",
        description: result.message,
        variant: "default",
      });
      
    } catch (error: any) {
      console.error("Twitter profile sync error:", error);
      setSyncProfilesStatus('error');
      setErrorDetails(error.message || "Unknown error");
      toast({
        title: "Sync Failed",
        description: error.message || "There was an error synchronizing Twitter profiles",
        variant: "destructive",
      });
    } finally {
      setSyncingProfiles(false);
    }
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Twitter KOL Profile Sync</CardTitle>
          <CardDescription>
            Synchronize Twitter KOL profiles to update profile images, follower counts, and verified status.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground mb-4">
            This will fetch profile data for all KOLs with Twitter social media type and update their profile information
            using the Twitter API. The following data will be updated:
          </p>
          
          <ul className="list-disc pl-5 text-sm text-muted-foreground mb-4 space-y-1">
            <li>Profile image URL</li>
            <li>Follower count</li>
            <li>Verified status</li>
          </ul>
        </CardContent>
        <CardFooter className="flex flex-col items-start space-y-4">
          <div className="flex items-center gap-2">
            <Button 
              onClick={handleSyncProfiles} 
              disabled={isSyncingProfiles}
              className="flex items-center gap-2"
            >
              {isSyncingProfiles ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Syncing Profiles...
                </>
              ) : (
                <>
                  <RefreshCw className="h-4 w-4" />
                  Sync KOL Profiles
                </>
              )}
              {syncProfilesStatus === 'success' && (
                <Check className="h-4 w-4 text-green-500 ml-2" />
              )}
              {syncProfilesStatus === 'error' && (
                <AlertCircle className="h-4 w-4 text-red-500 ml-2" />
              )}
            </Button>
          </div>
          
          {errorDetails && (
            <div className="w-full mt-2">
              <div className="bg-red-100 dark:bg-red-900/20 text-red-800 dark:text-red-200 p-3 rounded-md text-sm">
                <div className="font-medium mb-1">Error Details:</div>
                <div className="text-xs font-mono whitespace-pre-wrap break-words">{errorDetails}</div>
              </div>
            </div>
          )}
          
          {syncProfilesResult && (
            <div className="w-full mt-4 text-sm">
              <div className="bg-muted/50 rounded-md p-4">
                <div className="font-medium mb-2">Sync Results</div>
                <div className="grid grid-cols-3 gap-2 mb-2">
                  <div className="bg-green-100 dark:bg-green-900/30 p-2 rounded flex flex-col items-center">
                    <span className="font-bold text-lg">{syncProfilesResult.results.success}</span>
                    <span className="text-xs">Updated</span>
                  </div>
                  <div className="bg-yellow-100 dark:bg-yellow-900/30 p-2 rounded flex flex-col items-center">
                    <span className="font-bold text-lg">{syncProfilesResult.results.skipped}</span>
                    <span className="text-xs">Skipped</span>
                  </div>
                  <div className="bg-red-100 dark:bg-red-900/30 p-2 rounded flex flex-col items-center">
                    <span className="font-bold text-lg">{syncProfilesResult.results.failed}</span>
                    <span className="text-xs">Failed</span>
                  </div>
                </div>
                
                {syncProfilesResult.results.details?.length > 0 && (
                  <details className="mt-2">
                    <summary className="cursor-pointer text-xs font-medium">View Details</summary>
                    <div className="mt-2 max-h-60 overflow-y-auto">
                      {syncProfilesResult.results.details.map((detail: any, index: number) => (
                        <div 
                          key={index}
                          className={`text-xs p-2 mb-1 rounded ${
                            detail.status === 'success' 
                              ? 'bg-green-100 dark:bg-green-900/20' 
                              : detail.status === 'skipped' 
                                ? 'bg-yellow-100 dark:bg-yellow-900/20' 
                                : 'bg-red-100 dark:bg-red-900/20'
                          }`}
                        >
                          <div className="flex justify-between">
                            <span>
                              {detail.username ? `@${detail.username}` : detail.id}
                            </span>
                            <span className="font-medium">{detail.status}</span>
                          </div>
                          {detail.reason && (
                            <div className="text-muted-foreground mt-1">
                              {detail.reason}
                            </div>
                          )}
                          {detail.data && (
                            <div className="text-muted-foreground mt-1 text-[10px]">
                              Followers: {detail.data.followers}, 
                              Verified: {detail.data.verified_status ? 'Yes' : 'No'}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </details>
                )}
              </div>
            </div>
          )}
        </CardFooter>
      </Card>
    </div>
  );
};
